import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { memo, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Alert,
    List,
    Button,
    Box,
    Typography,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Stack,
    Chip,
    Divider,
    CircularProgress,
} from '@mui/material';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
dayjs.extend(minMax);
import HelpIconWithTooltip from '../../../../../components/HelpIconWithTooltip';
import ScheduleDate from './ScheduleDate';
import useConfirmDialog from '../../../../../hooks/useConfirmationDialog';

const EventSchedule = ({ fieldName, kansanopisto, setHours }) => {
    const { control, setValue, trigger, setError, clearErrors, getValues } = useFormContext();
    const { fields, replace } = useFieldArray({ name: fieldName, control });

    const aloitus = useWatch({ name: 'aloitus', defaultValue: { pvm: '' } });
    const lopetus = useWatch({ name: 'lopetus', defaultValue: { pvm: '' } });
    const currentSchedule = useWatch({ name: fieldName, defaultValue: [] });

    const [showReassignModal, setShowReassignModal] = useState(false);
    const [pendingDates, setPendingDates] = useState(null);
    const [tilaisuusAssignments, setTilaisuusAssignments] = useState({});
    const [overlapWarning, setOverlapWarning] = useState('');
    const [originalDates, setOriginalDates] = useState({ aloitus: '', lopetus: '' });
    const [cachedTilaisuudet, setCachedTilaisuudet] = useState([]);
    const [isReassigning, setIsReassigning] = useState(false);
    const [isSavingReassignment, setIsSavingReassignment] = useState(false);
    const [renderKey, setRenderKey] = useState(0);
    const modalActionRef = useRef(null);
    const modalLockRef = useRef(false);
    const debounceRef = useRef(null);

    const [ConfirmationDialog, confirmDateChange] = useConfirmDialog({
        title: 'Ohjelmaa ei voida säilyttää',
        message: 'Jotkin tilaisuudet eivät mahdu uuteen aikaväliin. Haluatko siirtää ne oletuspäiville?',
    });

    const datesAreSelected = !!(aloitus.pvm && lopetus.pvm);

    // Memoize getDateRange to prevent unnecessary recalculations
    const getDateRange = useCallback((start, end) => {
        if (!start || !end) {
            console.warn('EventSchedule: getDateRange: Invalid start or end date', { start, end });
            return [];
        }
        const startDate = dayjs(start, 'DD.MM.YYYY');
        const endDate = dayjs(end, 'DD.MM.YYYY');
        if (!startDate.isValid() || !endDate.isValid()) {
            console.warn('EventSchedule: getDateRange: Invalid date format', { start, end });
            return [];
        }
        if (endDate.isBefore(startDate)) {
            console.warn('EventSchedule: getDateRange: endDate before startDate', { start, end });
            return [];
        }
        // Ensure inclusive range (including last day)
        const range = [];
        let current = startDate.clone();
        while (current.isBefore(endDate, 'day') || current.isSame(endDate, 'day')) {
            range.push(current.format('DD.MM.YYYY'));
            current = current.add(1, 'day');
        }
        return range;
    }, []);

    // Cleanup effect at mount/unmount for schedules
    useEffect(() => {
        // Only run cleanup if there are actually invalid entries
        const hasInvalidEntries = currentSchedule.some((day) => !day || !day.pvm);
        if (!hasInvalidEntries) {
            return; // Schedule is already clean
        }

        const cleanedSchedule = currentSchedule
            .filter((day) => day && day.pvm) // Filter out null/undefined entries and entries without pvm
            .map((day) => ({
                ...day,
                tilaisuudet: day.tilaisuudet || [],
            }))
            .map((day, index) => ({
                ...day,
                dayIndex: index,
            }));
        // Update if we filtered something out or if schedule is now empty
        if (cleanedSchedule.length !== currentSchedule.length || cleanedSchedule.length === 0) {
            replace(cleanedSchedule);
            setValue(fieldName, cleanedSchedule, { shouldDirty: true });
            trigger(fieldName);
            setRenderKey((prev) => prev + 1);
        }
    }, [currentSchedule.length, currentSchedule.filter(day => day && day.pvm).length, replace, setValue, fieldName, trigger]);

    useEffect(() => {
        if (datesAreSelected) {
            const startDate = dayjs(aloitus.pvm, 'DD.MM.YYYY');
            const endDate = dayjs(lopetus.pvm, 'DD.MM.YYYY');
            if (startDate.isValid() && endDate.isValid() && endDate.isBefore(startDate)) {
                setError('lopetus.pvm', {
                    type: 'manual',
                    message: 'Lopetuspäivä ei voi olla ennen aloituspäivää',
                });
            } else {
                clearErrors('lopetus.pvm');
            }
        }
    }, [aloitus.pvm, lopetus.pvm, setError, clearErrors, datesAreSelected]);

    const mapScheduleToDates = useCallback((schedule, startDate) => {
        if (!startDate) {
            console.warn('EventSchedule: mapScheduleToDates: No startDate', { schedule });
            return schedule.map((day) => ({
                ...day,
                pvm: day.pvm || '',
                CircularProgress,
                tilaisuudet: day.tilaisuudet || [],
            }));
        }
        const start = dayjs(startDate, 'DD.MM.YYYY');
        if (!start.isValid()) {
            console.error('EventSchedule: mapScheduleToDates: Invalid startDate', { startDate });
            return schedule.map((day) => ({
                ...day,
                pvm: day.pvm || '',
                tilaisuudet: day.tilaisuudet || [],
            }));
        }
        const dateRange = getDateRange(startDate, lopetus.pvm);
        const mapped = dateRange.map((date, index) => {
            const existingDay = schedule.find((day) => day.pvm === date) || {
                dayIndex: index,
                pvm: date,
                tilaisuudet: dateRange.length === 1 ? schedule.flatMap(day => day.tilaisuudet || []) : [],
            };
            return {
                dayIndex: index,
                pvm: date,
                tilaisuudet: existingDay.tilaisuudet || [],
            };
        });
        return mapped;
    }, [getDateRange, lopetus.pvm]);

    const getAllTilaisuudet = useMemo(() => {
        const tilaisuudet = cachedTilaisuudet
            .filter((t) => t && t.tilaisuus && t.id)
            .map((tilaisuus) => ({
                id: tilaisuus.id,
                dayIndex: tilaisuus.dayIndex || 0,
                tilIndex: tilaisuus.tilIndex || 0,
                nimi: tilaisuus.nimi || `Tilaisuus ${tilaisuus.tilIndex + 1}`,
                aloitus: tilaisuus.tilaisuus.aloitus,
                lopetus: tilaisuus.tilaisuus.lopetus,
                tilaisuus: tilaisuus.tilaisuus,
            }));
        return tilaisuudet;
    }, [cachedTilaisuudet]);

    const reassignmentDateRange = useMemo(
        () => getDateRange(pendingDates?.aloitus, pendingDates?.lopetus),
        [getDateRange, pendingDates?.aloitus, pendingDates?.lopetus]
    );

    const assignmentBuckets = useMemo(() => {
        if (!reassignmentDateRange.length) {
            return [];
        }

        const buckets = reassignmentDateRange.map((date, dayIndex) => ({
            date,
            dayIndex,
            tilaisuudet: [],
        }));

        getAllTilaisuudet.forEach((tilaisuus) => {
            let dayIndex = tilaisuusAssignments[tilaisuus.id];
            if (dayIndex === undefined) {
                dayIndex = reassignmentDateRange.length === 1
                    ? '0'
                    : String(Math.min(tilaisuus.dayIndex, reassignmentDateRange.length - 1));
            }

            const parsedDayIndex = parseInt(dayIndex, 10);
            if (buckets[parsedDayIndex]) {
                buckets[parsedDayIndex].tilaisuudet.push(tilaisuus);
            }
        });

        return buckets;
    }, [getAllTilaisuudet, tilaisuusAssignments, reassignmentDateRange]);

    const checkOverlaps = useCallback((assignments, dateRange) => {
        const days = {};
        Object.entries(assignments).forEach(([id, dayIndex]) => {
            if (!days[dayIndex]) days[dayIndex] = [];
            const tilaisuus = cachedTilaisuudet.find((t) => t && t.id === id);
            if (tilaisuus) {
                days[dayIndex].push(tilaisuus.tilaisuus);
            }
        });

        for (const dayIndex in days) {
            const pvm = dateRange[parseInt(dayIndex)];
            if (!pvm || !dayjs(pvm, 'DD.MM.YYYY').isValid()) {
                continue;
            }
            const tilaisuudet = days[dayIndex];
            for (let i = 0; i < tilaisuudet.length; i++) {
                for (let j = i + 1; j < tilaisuudet.length; j++) {
                    const t1 = tilaisuudet[i];
                    const t2 = tilaisuudet[j];
                    const start1 = t1.aloitus && dayjs(t1.aloitus).isValid() ? dayjs(t1.aloitus) : null;
                    const end1 = t1.lopetus && dayjs(t1.lopetus).isValid() ? dayjs(t1.lopetus) : null;
                    const start2 = t2.aloitus && dayjs(t2.aloitus).isValid() ? dayjs(t2.aloitus) : null;
                    const end2 = t2.lopetus && dayjs(t2.lopetus).isValid() ? dayjs(t2.lopetus) : null;
                    if (
                        start1 &&
                        end1 &&
                        start2 &&
                        end2 &&
                        (start1.isBetween(start2, end2, null, '[]') ||
                            end1.isBetween(start2, end2, null, '[]') ||
                            start2.isBetween(start1, end1, null, '[]') ||
                            end2.isBetween(start1, end1, null, '[]'))
                    ) {
                        return `Varoitus: "${t1.nimi || 'Tilaisuus'}" (${start1.format('HH:mm')}-${end1.format('HH:mm')}) ja "${t2.nimi || 'Tilaisuus'}" (${start2.format('HH:mm')}-${end2.format('HH:mm')}) menevät päällekkäin päivänä ${pvm}`;
                    }
                }
            }
        }
        return '';
    }, [cachedTilaisuudet]);

    const triggerReassignment = () => {
        const actionId = Date.now();
        modalActionRef.current = actionId;
        modalLockRef.current = true;

        const newCachedTilaisuudet = currentSchedule
            .flatMap((day, dayIndex) =>
                (day.tilaisuudet || []).map((tilaisuus, tilIndex) => ({
                    id: tilaisuus.id || `${dayIndex}-${tilIndex}-${tilaisuus.nimi || tilIndex}-${actionId}`,
                    dayIndex,
                    tilIndex,
                    nimi: tilaisuus.nimi || `Tilaisuus ${tilIndex + 1}`,
                    tilaisuus,
                }))
            )
            .filter((t) => t && t.tilaisuus);

        const newDateRange = getDateRange(aloitus.pvm, lopetus.pvm);
        const initialAssignments = newCachedTilaisuudet.reduce((acc, tilaisuus) => {
            acc[tilaisuus.id] = newDateRange.length === 1 ? '0' : String(Math.min(tilaisuus.dayIndex, newDateRange.length - 1));
            return acc;
        }, {});


        setCachedTilaisuudet(newCachedTilaisuudet);
        setPendingDates({ aloitus: aloitus.pvm, lopetus: lopetus.pvm });
        setOriginalDates({ aloitus: aloitus.pvm, lopetus: lopetus.pvm });
        setTilaisuusAssignments(initialAssignments);
        setOverlapWarning(checkOverlaps(initialAssignments, newDateRange));
        setShowReassignModal(true);
        setIsReassigning(true);
    };

    const handleReassignConfirm = async () => {
        if (!pendingDates) {
            console.error('EventSchedule: handleReassignConfirm: No pendingDates');
            setShowReassignModal(false);
            setIsReassigning(false);
            setIsSavingReassignment(false);
            modalLockRef.current = false;
            return;
        }

        setIsReassigning(true);
        setIsSavingReassignment(true);
        await new Promise((resolve) => {
            window.requestAnimationFrame(() => {
                resolve();
            });
        });
        const actionId = Date.now();
        modalActionRef.current = actionId;

        const newDateRange = getDateRange(pendingDates.aloitus, pendingDates.lopetus);
        if (!newDateRange.length) {
            console.error('EventSchedule: handleReassignConfirm: Empty date range', { pendingDates });
            setShowReassignModal(false);
            setIsReassigning(false);
            setIsSavingReassignment(false);
            modalLockRef.current = false;
            return;
        }

        const newSchedule = Array.from({ length: newDateRange.length }, (_, i) => ({
            dayIndex: i,
            pvm: newDateRange[i],
            tilaisuudet: [],
        }));

        getAllTilaisuudet.forEach((tilaisuus) => {
            let targetDayIndex = tilaisuusAssignments[tilaisuus.id];
            if (targetDayIndex === undefined) {
                targetDayIndex = newDateRange.length === 1 ? '0' : String(Math.min(tilaisuus.dayIndex, newDateRange.length - 1));
            }
            const dayIdx = parseInt(targetDayIndex);
            const targetDate = dayjs(newDateRange[dayIdx], 'DD.MM.YYYY');
            if (newSchedule[dayIdx]) {
                const originalStart = dayjs(tilaisuus.tilaisuus.aloitus);
                const originalEnd = tilaisuus.tilaisuus.lopetus ? dayjs(tilaisuus.tilaisuus.lopetus) : null;
                const newStart = targetDate
                    .set('hour', originalStart.hour())
                    .set('minute', originalStart.minute())
                    .set('second', originalStart.second());
                const newEnd = originalEnd
                    ? targetDate
                        .set('hour', originalEnd.hour())
                        .set('minute', originalEnd.minute())
                        .set('second', originalEnd.second())
                    : null;
                newSchedule[dayIdx].tilaisuudet.push({
                    ...tilaisuus.tilaisuus,
                    id: tilaisuus.id,
                    aloitus: newStart.toISOString(),
                    lopetus: newEnd ? newEnd.toISOString() : null,
                });
            }
        });

        newSchedule.forEach((day) => {
            day.tilaisuudet.sort((a, b) => {
                const timeA = a.aloitus && dayjs(a.aloitus).isValid()
                    ? dayjs(a.aloitus)
                    : dayjs().set('hour', 23).set('minute', 59);
                const timeB = b.aloitus && dayjs(b.aloitus).isValid()
                    ? dayjs(b.aloitus)
                    : dayjs().set('hour', 23).set('minute', 59);
                return timeA.diff(timeB);
            });
        });

        const updatedSchedule = newSchedule.map((day, index) => ({
            ...day,
            dayIndex: index,
        }));


        replace(updatedSchedule);
        setValue(fieldName, updatedSchedule, { shouldDirty: true, shouldTouch: true });
        await trigger(fieldName);
        updatedSchedule.forEach((day, index) => {
            trigger(`${fieldName}[${index}].tilaisuudet`);
        });

        setShowReassignModal(false);
        setPendingDates(null);
        setTilaisuusAssignments({});
        setCachedTilaisuudet(
            updatedSchedule.flatMap((day, dayIndex) =>
                (day.tilaisuudet || []).map((tilaisuus, tilIndex) => ({
                    id: tilaisuus.id,
                    dayIndex,
                    tilIndex,
                    nimi: tilaisuus.nimi,
                    tilaisuus,
                }))
            )
        );
        setOverlapWarning('');
        setIsReassigning(false);
        setIsSavingReassignment(false);
        modalLockRef.current = false;
        setRenderKey((prev) => prev + 1);
    };

    const handleReassignCancel = () => {
        if (modalLockRef.current) {
            return;
        }
        const actionId = Date.now();
        modalActionRef.current = actionId;

        setValue('aloitus.pvm', originalDates.aloitus, { shouldDirty: true });
        setValue('lopetus.pvm', originalDates.lopetus, { shouldDirty: true });
        setShowReassignModal(false);
        setPendingDates(null);
        setTilaisuusAssignments({});
        setCachedTilaisuudet(
            currentSchedule.flatMap((day, dayIndex) =>
                (day.tilaisuudet || []).map((tilaisuus, tilIndex) => ({
                    id: tilaisuus.id,
                    dayIndex,
                    tilIndex,
                    nimi: tilaisuus.nimi,
                    tilaisuus,
                }))
            )
        );
        setOverlapWarning('');
        setIsReassigning(false);
        setIsSavingReassignment(false);
        setRenderKey((prev) => prev + 1);
    };

    const getAssignmentSummary = () => {
        if (!pendingDates || !showReassignModal) {
            return 'Ei tilaisuuksia määritetty';
        }
        if (!assignmentBuckets.length) {
            console.warn('EventSchedule: getAssignmentSummary: Empty date range');
            return 'Ei tilaisuuksia määritetty';
        }

        const result = assignmentBuckets
            .map(
                (day) =>
                    `Päivä ${day.dayIndex + 1} (${day.date}): ${
                        day.tilaisuudet.length ? day.tilaisuudet.map((tilaisuus) => tilaisuus.nimi).join(', ') : 'Ei tilaisuuksia'
                    }`
            )
            .join('; ');
        return result;
    };

    useEffect(() => {
        if (!datesAreSelected || isReassigning || modalLockRef.current) {
            return;
        }

        // Clear any existing debounce
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        // Debounce the modal check to allow EventForm to synchronize lopetus.pvm
        debounceRef.current = setTimeout(() => {
            const newDateRange = getDateRange(aloitus.pvm, lopetus.pvm);
            const newDayCount = newDateRange.length;
            const currentDayCount = currentSchedule.length;
            const isSingleDayEvent = aloitus.pvm === lopetus.pvm;

            if (!newDateRange.length) {
                console.warn('EventSchedule: useEffect: Invalid date range, skipping modal', { aloitus: aloitus.pvm, lopetus: lopetus.pvm });
                return;
            }

            if (!currentSchedule.length && newDayCount > 0) {
                const initialSchedule = newDateRange.map((date, index) => ({
                    dayIndex: index,
                    pvm: date,
                    tilaisuudet: [],
                }));
                replace(initialSchedule);
                setValue(fieldName, initialSchedule, { shouldDirty: true });
                trigger(fieldName);
                setRenderKey((prev) => prev + 1);
                return;
            }

            if (isSingleDayEvent || newDayCount === 1) {
                // Single-day event: consolidate all tilaisuudet into one day without showing modal
                const singleDaySchedule = [{
                    dayIndex: 0,
                    pvm: newDateRange[0],
                    tilaisuudet: currentSchedule.flatMap(day => day.tilaisuudet || []),
                }];
                replace(singleDaySchedule);
                setValue(fieldName, singleDaySchedule, { shouldDirty: true });
                trigger(fieldName);
                setPendingDates(null);
                setOriginalDates({ aloitus: aloitus.pvm, lopetus: lopetus.pvm });
                setCachedTilaisuudet(
                    singleDaySchedule.flatMap((day, dayIndex) =>
                        (day.tilaisuudet || []).map((tilaisuus, tilIndex) => ({
                            id: tilaisuus.id,
                            dayIndex,
                            tilIndex,
                            nimi: tilaisuus.nimi,
                            tilaisuus,
                        }))
                    )
                );
                setRenderKey((prev) => prev + 1);
                return;
            }

            if (currentDayCount !== newDayCount && newDayCount > 1) {
                const actionId = Date.now();
                modalActionRef.current = actionId;
                modalLockRef.current = true;

                const newCachedTilaisuudet = currentSchedule
                    .flatMap((day, dayIndex) =>
                        (day.tilaisuudet || []).map((tilaisuus, tilIndex) => ({
                            id: tilaisuus.id || `${dayIndex}-${tilIndex}-${tilaisuus.nimi || tilIndex}-${actionId}`,
                            dayIndex,
                            tilIndex,
                            nimi: tilaisuus.nimi || `Tilaisuus ${tilIndex + 1}`,
                            tilaisuus,
                        }))
                    )
                    .filter((t) => t && t.tilaisuus);

                const initialAssignments = newCachedTilaisuudet.reduce((acc, tilaisuus) => {
                    acc[tilaisuus.id] = String(Math.min(tilaisuus.dayIndex, newDayCount - 1));
                    return acc;
                }, {});

                setCachedTilaisuudet(newCachedTilaisuudet);
                setPendingDates({ aloitus: aloitus.pvm, lopetus: lopetus.pvm });
                setOriginalDates({ aloitus: aloitus.pvm, lopetus: lopetus.pvm });
                setTilaisuusAssignments(initialAssignments);
                setShowReassignModal(true);
                setIsReassigning(true);
            }
        }, 100); // 100ms debounce to allow EventForm synchronization

        // Cleanup debounce on unmount or dependency change
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [aloitus.pvm, lopetus.pvm, currentSchedule.length, replace, setValue, datesAreSelected, fieldName, trigger, isReassigning]);

    const sortTilaisuudetByStartTime = async () => {
        const sortedSchedule = currentSchedule.map((day) => ({
            ...day,
            tilaisuudet: [...(day.tilaisuudet || [])].sort((a, b) => {
                const timeA = a.aloitus && dayjs(a.aloitus).isValid()
                    ? dayjs(a.aloitus)
                    : dayjs().set('hour', 23).set('minute', 59);
                const timeB = b.aloitus && dayjs(b.aloitus).isValid()
                    ? dayjs(b.aloitus)
                    : dayjs().set('hour', 23).set('minute', 59);
                return timeA.diff(timeB);
            }),
        }));
        replace(sortedSchedule);
        setValue(fieldName, sortedSchedule, { shouldDirty: true });
        await trigger(fieldName);
        setRenderKey((prev) => prev + 1);
    };

    if (!datesAreSelected && !currentSchedule.length) {
        return (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
                Valitse tapahtumalle päivämäärä muokataksesi sen ohjelmaa
            </Alert>
        );
    }

    // Don't use mapScheduleToDates for rendering - it creates phantom entries
    // Just render what's actually in the form state
    const renderedSchedule = currentSchedule.filter(day => day && day.pvm);

    const formatDateOptionLabel = (date, index) => `Päivä ${index + 1} • ${date}`;

    const formatTimeRange = (tilaisuus) => {
        const startLabel = tilaisuus.aloitus && dayjs(tilaisuus.aloitus).isValid()
            ? dayjs(tilaisuus.aloitus).format('HH:mm')
            : 'ei aikaa';
        const endLabel = tilaisuus.lopetus && dayjs(tilaisuus.lopetus).isValid()
            ? dayjs(tilaisuus.lopetus).format('HH:mm')
            : 'ei aikaa';

        return `${startLabel} - ${endLabel}`;
    };

    const getCurrentAssignmentIndex = (tilaisuus) => {
        if (!reassignmentDateRange.length) {
            return 0;
        }

        return Math.min(tilaisuus.dayIndex || 0, reassignmentDateRange.length - 1);
    };

    return (
        <>
            <List sx={{ borderRadius: 3 }} id="event-schedule">
                {renderedSchedule.map((field, index) => {
                    return (
                        <ScheduleDate
                            setHours={setHours}
                            kansanopisto={kansanopisto}
                            key={`${field.dayIndex}-${field.pvm || 'no-pvm'}-${index}-${renderKey}`}
                            date={field.pvm || ''}
                            nestedIndex={index}
                            control={control}
                            fieldName={fieldName}
                            subfieldName="tilaisuudet"
                            isReassigning={isReassigning}
                        />
                    );
                })}
                {ConfirmationDialog()}
            </List>
            <Button
                variant="contained"
                onClick={sortTilaisuudetByStartTime}
                sx={{ backgroundColor: 'taphaBlue.main', whiteSpace: 'nowrap', px: 3, mt: 1 }}
            >
                Järjestä tilaisuudet
            </Button>
            <Button
                variant="contained"
                onClick={triggerReassignment}
                sx={{ backgroundColor: 'taphaBlue.main', whiteSpace: 'nowrap', px: 3, mt: 1 }}
            >
                Muokkaa tilaisuuksien päiviä
            </Button>
            <HelpIconWithTooltip
                text="Jos tilaisuudet ovat epäjärjestyksessä, järjestä ne. Muokkaa tilaisuuksien päiviä tarvittaessa."
                iconSize={20}
                tooltipFontSize="16px"
            />
            <Dialog
                open={showReassignModal}
                onClose={handleReassignCancel}
                fullWidth
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        px: { xs: 3, md: 4 },
                        py: 3,
                        background: 'linear-gradient(135deg, #0E4C92 0%, #1E88E5 100%)',
                        color: 'white',
                    }}
                >
                    <Stack spacing={1.25}>
                        <Typography variant="h5" fontWeight={700}>
                            Määritä tilaisuudet uusille päiville
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.92, maxWidth: 820 }}>
                            Käy jokainen tilaisuus läpi vasemmalta ja valitse sille uusi päivä. Oikealla näet heti,
                            miltä ohjelma näyttää päivittäin ja onko samalla päivällä päällekkäisiä aikoja.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Chip
                                label={`${getAllTilaisuudet.length} tilaisuutta`}
                                sx={{
                                    alignSelf: 'flex-start',
                                    bgcolor: 'rgba(255,255,255,0.16)',
                                    color: 'white',
                                    fontWeight: 600,
                                }}
                            />
                            <Chip
                                label={`${reassignmentDateRange.length} päivää käytössä`}
                                sx={{
                                    alignSelf: 'flex-start',
                                    bgcolor: 'rgba(255,255,255,0.16)',
                                    color: 'white',
                                    fontWeight: 600,
                                }}
                            />
                        </Stack>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, md: 4 }, py: 3, bgcolor: '#F7FAFC' }}>
                    {overlapWarning && (
                        <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
                            {overlapWarning}
                        </Alert>
                    )}
                    {!getAllTilaisuudet.length ? (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 4,
                                borderRadius: 3,
                                textAlign: 'center',
                                bgcolor: 'white',
                            }}
                        >
                            <Typography variant="h6" mb={1}>
                                Ei tilaisuuksia määritettäväksi
                            </Typography>
                            <Typography color="text.secondary">
                                Lisää ensin ohjelmaan tilaisuuksia, niin voit siirtää ne eri päiville tässä näkymässä.
                            </Typography>
                        </Paper>
                    ) : (
                        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="stretch">
                            <Stack spacing={2} sx={{ flex: 1.25, minWidth: 0 }}>
                                {getAllTilaisuudet.map((tilaisuus, index) => {
                                    const currentDayIndex = getCurrentAssignmentIndex(tilaisuus);
                                    const selectedDayIndex = tilaisuusAssignments[tilaisuus.id] !== undefined
                                        ? tilaisuusAssignments[tilaisuus.id]
                                        : String(currentDayIndex);

                                    return (
                                        <Paper
                                            key={tilaisuus.id}
                                            elevation={0}
                                            sx={{
                                                p: 2.5,
                                                borderRadius: 3,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                bgcolor: 'white',
                                            }}
                                        >
                                            <Stack spacing={2}>
                                                <Stack
                                                    direction={{ xs: 'column', md: 'row' }}
                                                    justifyContent="space-between"
                                                    spacing={2}
                                                >
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
                                                            <Chip
                                                                label={`Tilaisuus ${index + 1}`}
                                                                size="small"
                                                                sx={{ fontWeight: 700, bgcolor: '#E3F2FD', color: '#0D47A1' }}
                                                            />
                                                            <Typography variant="subtitle2" color="text.secondary">
                                                                {formatTimeRange(tilaisuus)}
                                                            </Typography>
                                                        </Stack>
                                                        <Typography variant="h6" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                                                            {tilaisuus.nimi}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ minWidth: { xs: '100%', md: 220 } }}>
                                                        <Typography
                                                            variant="overline"
                                                            sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}
                                                        >
                                                            Uusi päivä
                                                        </Typography>
                                                        <Select
                                                            fullWidth
                                                            value={selectedDayIndex}
                                                            onChange={(e) => {
                                                                const newAssignments = {
                                                                    ...tilaisuusAssignments,
                                                                    [tilaisuus.id]: e.target.value,
                                                                };
                                                                setTilaisuusAssignments(newAssignments);
                                                                setOverlapWarning(
                                                                    checkOverlaps(newAssignments, reassignmentDateRange)
                                                                );
                                                            }}
                                                            size="small"
                                                            sx={{ mt: 0.5, bgcolor: 'white' }}
                                                        >
                                                            {reassignmentDateRange.map((date, idx) => (
                                                                <MenuItem key={date} value={String(idx)}>
                                                                    {formatDateOptionLabel(date, idx)}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </Box>
                                                </Stack>
                                                <Divider />
                                                <Stack
                                                    direction={{ xs: 'column', md: 'row' }}
                                                    spacing={1.5}
                                                    alignItems={{ xs: 'flex-start', md: 'center' }}
                                                >
                                                    <Chip
                                                        label={`Nykyinen: ${formatDateOptionLabel(reassignmentDateRange[currentDayIndex], currentDayIndex)}`}
                                                        variant="outlined"
                                                        sx={{ borderRadius: 2 }}
                                                    />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Valitse päivä, jolle tämän tilaisuuden kellonaika siirretään.
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </Paper>
                                    );
                                })}
                            </Stack>
                            <Paper
                                elevation={0}
                                sx={{
                                    flex: 0.9,
                                    p: 3,
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'white',
                                    alignSelf: 'stretch',
                                }}
                            >
                                <Stack spacing={2.5}>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700} mb={0.5}>
                                            Päiväkohtainen yhteenveto
                                        </Typography>
                                        <Typography color="text.secondary">
                                            Näet tästä heti, kuinka monta tilaisuutta kullakin päivällä on valintojesi jälkeen.
                                        </Typography>
                                    </Box>
                                    <Stack spacing={1.5}>
                                        {assignmentBuckets.map((day) => (
                                            <Paper
                                                key={day.date}
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 3,
                                                    bgcolor: day.tilaisuudet.length ? '#F8FBFF' : '#FCFCFD',
                                                }}
                                            >
                                                <Stack spacing={1.25}>
                                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                                        <Box>
                                                            <Typography variant="subtitle1" fontWeight={700}>
                                                                {formatDateOptionLabel(day.date, day.dayIndex)}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {day.tilaisuudet.length
                                                                    ? `${day.tilaisuudet.length} tilaisuutta tällä päivällä`
                                                                    : 'Ei tilaisuuksia tällä päivällä'}
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            label={day.tilaisuudet.length === 1 ? '1 tilaisuus' : `${day.tilaisuudet.length} tilaisuutta`}
                                                            color={day.tilaisuudet.length ? 'primary' : 'default'}
                                                            variant={day.tilaisuudet.length ? 'filled' : 'outlined'}
                                                            sx={{ fontWeight: 700 }}
                                                        />
                                                    </Stack>
                                                    {day.tilaisuudet.length > 0 && (
                                                        <Stack spacing={1}>
                                                            {day.tilaisuudet.map((tilaisuus) => (
                                                                <Box
                                                                    key={`${day.date}-${tilaisuus.id}`}
                                                                    sx={{
                                                                        p: 1.5,
                                                                        borderRadius: 2,
                                                                        bgcolor: 'rgba(30, 136, 229, 0.06)',
                                                                    }}
                                                                >
                                                                    <Typography fontWeight={600}>{tilaisuus.nimi}</Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {formatTimeRange(tilaisuus)}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </Stack>
                                    <Alert severity="info" sx={{ borderRadius: 3 }}>
                                        {getAssignmentSummary()}
                                    </Alert>
                                </Stack>
                            </Paper>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: { xs: 2, md: 4 }, py: 2.5, bgcolor: 'white' }}>
                    <Button onClick={handleReassignCancel} color="inherit" disabled={isSavingReassignment}>
                        Peruuta
                    </Button>
                    <Button
                        onClick={handleReassignConfirm}
                        variant="contained"
                        color="primary"
                        disabled={isSavingReassignment}
                        sx={{ minWidth: 260, minHeight: 44 }}
                    >
                        {isSavingReassignment ? (
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <CircularProgress size={26} thickness={5} color="inherit" />
                                <Box component="span" sx={{ fontWeight: 700 }}>
                                    Tallennetaan...
                                </Box>
                            </Stack>
                        ) : (
                            'Tallenna päivämuutokset'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

EventSchedule.propTypes = {
    fieldName: PropTypes.string,
    kansanopisto: PropTypes.any,
    setHours: PropTypes.func,
};

export default memo(EventSchedule);